import React, { useEffect, useState } from 'react';
import styles from './Filter.module.scss';

import DialogWrapper from 'components/DialogComponents/DialogWrapper';
import { FormDayPicker, FormLabel, FormRow } from 'components/FormComponents';
import { FilterActions, FilterHeader } from 'components/FilterComponentsV2';
import FormAutoMultiSelect from 'components/FormComponents/FormAutoMultiSelect/FormAutoMultiSelect';
import FormMultiSelect from 'components/FormComponents/FormMultiSelect/FormMultiSelect';

import { FormProvider, useForm } from 'react-hook-form';
import { useMobileViewport } from 'hooks/useMobileViewport';
import { usePicklistDetailsSelector } from 'hooks/Picklist';

import { enrichNameToId } from 'helpers/AppHelpers';

import { defaultFilter, processSublocations } from '../../../../helpers';

export function Filter({ open, setOpen, onApply, filter }) {
  const isMobile = useMobileViewport();

  const [sublocationOptions, setSublocationOptions] = useState([]);

  const {
    currentPicklist: { createdAtUtc },
    criteria
  } = usePicklistDetailsSelector();

  const methods = useForm({
    defaultValues: { ...defaultFilter },
    mode: 'onChange'
  });
  const { getValues, reset, setValue, watch } = methods;

  const {
    locationsWithSublocations,
    billOfLadings,
    picklistAssetStatuses,
    trackAndTrailerAssetDrCodes
  } = criteria;

  useEffect(() => {
    if (open) {
      const { options } = processSublocations(filter.filters, criteria);
      setSublocationOptions(options);
      reset(filter.filters);
    }
  }, [open]);

  const closePopup = () => setOpen(false);

  const applyFilter = () => {
    onApply(getValues());
    closePopup();
  };

  const clearFilter = () => reset({ ...defaultFilter });

  const handleMultiSelect = (name, value) => setValue(name, value);

  const handleLocationSelect = () => {
    const { fieldValue, options } = processSublocations(getValues(), criteria);

    setValue('originSublocationIds', fieldValue);
    setSublocationOptions(options);
  };

  const dateFromWatcher = watch('pickUpDateFrom');
  const dateToWatcher = watch('pickUpDateTo');

  return (
    <DialogWrapper open={open} onClose={closePopup}>
      <div className={styles.dialog}>
        {isMobile && <FilterHeader title="Filters" onBack={closePopup} />}
        <FormProvider {...methods}>
          <div className={styles.form}>
            <div className={styles.form__block}>
              <FormRow>
                <FormLabel>Origin Location</FormLabel>
                <FormAutoMultiSelect
                  name="originLocationIds"
                  menuItems={locationsWithSublocations || []}
                  options={{
                    label: 'siteCode',
                    disableByObjectTracker: true,
                    disableLabel: true,
                    extraAction: handleLocationSelect
                  }}
                />
              </FormRow>
              <FormRow>
                <FormLabel>Job Number</FormLabel>
                <FormAutoMultiSelect
                  name="originLocationJobNumberIds"
                  menuItems={locationsWithSublocations || []}
                  options={{
                    label: 'locationJobNumber',
                    disableByObjectTracker: true,
                    disableLabel: true,
                    extraAction: handleLocationSelect
                  }}
                />
              </FormRow>
              <FormRow>
                <FormLabel>Origin Sublocation</FormLabel>
                <FormAutoMultiSelect
                  name="originSublocationIds"
                  menuItems={sublocationOptions || []}
                  options={{
                    label: 'name',
                    disableByObjectTracker: true,
                    disableLabel: true,
                    isDisabled: !sublocationOptions?.length
                  }}
                />
              </FormRow>
              <FormRow>
                <FormLabel>Type</FormLabel>
                <FormMultiSelect
                  trackById
                  name="picklistAssetStatusNames"
                  options={enrichNameToId(picklistAssetStatuses) || []}
                  onSelect={handleMultiSelect}
                />
              </FormRow>
            </div>
            <div className={styles.form__block}>
              <FormRow>
                <FormLabel>Freight Bill</FormLabel>
                <FormAutoMultiSelect
                  name="billOfLadingIds"
                  menuItems={billOfLadings || []}
                  options={{
                    label: 'freightBill',
                    disableByObjectTracker: true,
                    disableLabel: true
                  }}
                />
              </FormRow>
              <FormRow>
                <FormLabel>Truck or Trailer</FormLabel>
                <FormAutoMultiSelect
                  name="trackAndTrailerAssetDrCodes"
                  menuItems={
                    trackAndTrailerAssetDrCodes?.map((str) => ({ id: str, name: str })) || []
                  }
                  options={{
                    label: 'name',
                    disableByObjectTracker: true,
                    disableLabel: true
                  }}
                />
              </FormRow>
              <FormRow>
                <FormLabel>Pick Up date from</FormLabel>
                <FormDayPicker name="pickUpDateFrom" min={createdAtUtc} max={dateToWatcher} />
              </FormRow>
              <FormRow>
                <FormLabel>Pick Up date to</FormLabel>
                <FormDayPicker name="pickUpDateTo" min={dateFromWatcher || createdAtUtc} />
              </FormRow>
            </div>
          </div>
        </FormProvider>
        <FilterActions onClear={clearFilter} onCancel={closePopup} onApply={applyFilter} />
      </div>
    </DialogWrapper>
  );
}
